import React, { useState } from 'react';
import type { BlockNode } from '../../types';
import { useSiteNavigation } from '../../context/SiteNavigationContext';
import { Menu, X, Phone, ArrowRight, ShieldCheck, Mail, MapPin, GraduationCap } from 'lucide-react';

interface SiteNavbarProps {
  variant?: 'classic' | 'centered' | 'glass' | 'split' | 'dark' | 'minimal';
  brandName?: string;
  logoText?: string;
  logoUrl?: string;
  tagline?: string;
  ctaText?: string;
  ctaLink?: string;
  phone?: string;
  sticky?: boolean;
  accentColor?: string;
  bgColor?: string;
  textColor?: string;
}

export const SiteNavbarRenderer: React.FC<{ node: BlockNode }> = ({ node }) => {
  const {
    variant = 'classic',
    brandName = 'Apex Coaching Academy',
    logoText = 'AC',
    logoUrl,
    tagline = 'IIT-JEE · NEET · Foundation',
    ctaText = 'Student Login / Enroll',
    ctaLink = '#enroll',
    phone = '+91 98765 43210',
    sticky = true,
    accentColor = '#2563eb',
    bgColor = '#ffffff',
    textColor = '#0f172a',
  } = (node.props as SiteNavbarProps) || {};

  const { pages, activePageId, onNavigatePage } = useSiteNavigation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Student-visible pages list
  const navPages = pages.length > 0
    ? pages
    : [
        { id: 'home', title: 'Home', route_path: '/' },
        { id: 'courses', title: 'Courses', route_path: '/courses' },
        { id: 'results', title: 'Results & Toppers', route_path: '/results' },
        { id: 'faculty', title: 'Faculty', route_path: '/faculty' },
        { id: 'admissions', title: 'Admissions', route_path: '/admissions' },
      ];

  const handleLinkClick = (e: React.MouseEvent, pageId: string, path: string) => {
    e.preventDefault();
    onNavigatePage(pageId, path);
    setMobileMenuOpen(false);
  };

  const isDark = variant === 'dark';
  const isGlass = variant === 'glass';
  const isCentered = variant === 'centered';
  const isMinimal = variant === 'minimal';
  const isSplit = variant === 'split';

  const navbarBg = isDark
    ? '#0f172a'
    : isGlass
    ? 'rgba(255, 255, 255, 0.85)'
    : bgColor;
  const navbarText = isDark ? '#f8fafc' : textColor;

  return (
    <header
      className={`w-full z-40 transition-colors ${
        sticky ? 'sticky top-0' : 'relative'
      } ${isGlass ? 'backdrop-blur-md shadow-xs border-b border-border/40' : 'border-b border-border/60 shadow-xs'}`}
      style={{
        backgroundColor: navbarBg,
        color: navbarText,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between h-14 sm:h-16 ${isCentered ? 'flex-col sm:flex-row py-2' : ''}`}>
          {/* Brand Logo & Name */}
          <div className="flex items-center gap-3 shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-9 w-auto object-contain rounded" />
            ) : (
              <div
                className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg flex items-center justify-center font-bold text-white text-xs sm:text-sm shadow-xs"
                style={{ backgroundColor: accentColor }}
              >
                {logoText}
              </div>
            )}
            <div className="flex flex-col">
              <span className="font-bold text-sm sm:text-base leading-tight tracking-tight">
                {brandName}
              </span>
              {tagline && !isMinimal && (
                <span className="text-[10px] opacity-75 font-medium leading-none mt-0.5">
                  {tagline}
                </span>
              )}
            </div>
          </div>

          {/* Desktop Nav Links (Student-Visible Pages) */}
          <nav
            className={`hidden md:flex items-center gap-1 lg:gap-2 ${
              isCentered ? 'mx-auto' : isSplit ? 'mx-6 flex-1 justify-center' : 'ml-auto mr-4'
            }`}
          >
            {navPages.map((p) => {
              const isActive = p.id === activePageId || p.route_path === '/';
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={(e) => handleLinkClick(e, p.id, p.route_path)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                    isActive
                      ? 'text-primary bg-primary/10 font-bold'
                      : 'hover:bg-black/5 dark:hover:bg-white/5 opacity-85 hover:opacity-100'
                  }`}
                  style={{ color: isActive ? accentColor : 'inherit' }}
                >
                  {p.title}
                </button>
              );
            })}
          </nav>

          {/* Right Action / Contact & CTA */}
          <div className="hidden sm:flex items-center gap-3 shrink-0">
            {phone && !isMinimal && (
              <a
                href={`tel:${phone}`}
                className="text-[11px] font-medium opacity-80 hover:opacity-100 flex items-center gap-1.5"
              >
                <Phone className="h-3 w-3" style={{ color: accentColor }} />
                <span>{phone}</span>
              </a>
            )}
            {ctaText && (
              <a
                href={ctaLink}
                className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-white shadow-xs transition-transform hover:scale-[1.02] flex items-center gap-1"
                style={{ backgroundColor: accentColor }}
              >
                <span>{ctaText}</span>
                <ArrowRight className="h-3 w-3" />
              </a>
            )}
          </div>

          {/* Mobile Hamburger Toggle */}
          <div className="flex items-center gap-2 md:hidden">
            {ctaText && (
              <a
                href={ctaLink}
                className="px-2.5 py-1 rounded text-[11px] font-bold text-white shadow-xs"
                style={{ backgroundColor: accentColor }}
              >
                Enroll
              </a>
            )}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 rounded-md hover:bg-black/5 dark:hover:bg-white/10 text-inherit cursor-pointer"
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border/40 py-3 space-y-1 animate-in fade-in slide-in-from-top-2 duration-150">
            {navPages.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={(e) => handleLinkClick(e, p.id, p.route_path)}
                className="w-full text-left px-3 py-2 rounded text-xs font-semibold hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-between"
              >
                <span>{p.title}</span>
                <ArrowRight className="h-3 w-3 opacity-50" />
              </button>
            ))}
            {phone && (
              <div className="px-3 py-2 text-[11px] text-muted-foreground flex items-center gap-2 border-t border-border/30 mt-2">
                <Phone className="h-3 w-3" />
                <span>Admissions: {phone}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

interface SiteFooterProps {
  variant?: 'multicolumn' | 'centered' | 'newsletter' | 'dark' | 'compact';
  brandName?: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  copyrightText?: string;
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
}

export const SiteFooterRenderer: React.FC<{ node: BlockNode }> = ({ node }) => {
  const {
    variant = 'multicolumn',
    brandName = 'Apex Coaching Academy',
    description = 'Premier coaching institute empowering students to achieve top national ranks in JEE, NEET, and Olympiad examinations.',
    address = 'Knowledge Park, Sector 62, New Delhi - 110001',
    phone = '+91 98765 43210',
    email = 'admissions@academy.edu.in',
    copyrightText = '© 2026 All Rights Reserved.',
    bgColor = '#0f172a',
    textColor = '#f8fafc',
    accentColor = '#3b82f6',
  } = (node.props as SiteFooterProps) || {};

  const { pages, onNavigatePage } = useSiteNavigation();

  const navPages = pages.length > 0
    ? pages
    : [
        { id: 'home', title: 'Home', route_path: '/' },
        { id: 'courses', title: 'Courses', route_path: '/courses' },
        { id: 'results', title: 'Results & Toppers', route_path: '/results' },
        { id: 'faculty', title: 'Faculty', route_path: '/faculty' },
        { id: 'admissions', title: 'Admissions', route_path: '/admissions' },
      ];

  const isCentered = variant === 'centered';
  const isCompact = variant === 'compact';
  const isNewsletter = variant === 'newsletter';

  return (
    <footer
      className="w-full border-t border-white/10 select-none"
      style={{
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {isCompact ? (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-2 font-bold">
              <GraduationCap className="h-4 w-4" style={{ color: accentColor }} />
              <span>{brandName}</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-[11px] opacity-80">
              {navPages.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onNavigatePage(p.id, p.route_path)}
                  className="hover:underline hover:opacity-100 cursor-pointer"
                >
                  {p.title}
                </button>
              ))}
            </div>
            <div className="text-[10px] opacity-60">{copyrightText}</div>
          </div>
        ) : isCentered ? (
          <div className="flex flex-col items-center text-center max-w-2xl mx-auto space-y-4">
            <div className="flex items-center gap-2 text-lg font-bold">
              <GraduationCap className="h-5 w-5" style={{ color: accentColor }} />
              <span>{brandName}</span>
            </div>
            <p className="text-xs opacity-75 leading-relaxed">{description}</p>
            <div className="flex flex-wrap justify-center gap-4 text-xs font-semibold py-2">
              {navPages.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onNavigatePage(p.id, p.route_path)}
                  className="hover:opacity-100 opacity-80 cursor-pointer transition-opacity"
                  style={{ color: 'inherit' }}
                >
                  {p.title}
                </button>
              ))}
            </div>
            <div className="text-[11px] opacity-70 flex flex-wrap justify-center gap-4 pt-2 border-t border-white/10 w-full">
              <span>{phone}</span>
              <span>•</span>
              <span>{email}</span>
            </div>
            <div className="text-[10px] opacity-50 pt-2">{copyrightText}</div>
          </div>
        ) : (
          /* Multi-column & Newsletter Variants */
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            {/* Col 1: Institute Info */}
            <div className="md:col-span-1 space-y-3">
              <div className="flex items-center gap-2 font-bold text-base">
                <GraduationCap className="h-5 w-5" style={{ color: accentColor }} />
                <span>{brandName}</span>
              </div>
              <p className="text-xs opacity-75 leading-relaxed">{description}</p>
              <div className="flex items-center gap-1.5 text-[10px] opacity-60 pt-1">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                <span>Government Registered Coaching Institute</span>
              </div>
            </div>

            {/* Col 2: Student Portal Pages */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-3 opacity-90">
                Student Portal
              </h4>
              <ul className="space-y-2 text-xs opacity-80">
                {navPages.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => onNavigatePage(p.id, p.route_path)}
                      className="hover:underline hover:opacity-100 cursor-pointer transition-opacity"
                    >
                      {p.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3: Campus & Admissions */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-3 opacity-90">
                Campus & Helpline
              </h4>
              <div className="space-y-2.5 text-xs opacity-80">
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: accentColor }} />
                  <span className="leading-tight">{address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 shrink-0" style={{ color: accentColor }} />
                  <span>{phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 shrink-0" style={{ color: accentColor }} />
                  <span>{email}</span>
                </div>
              </div>
            </div>

            {/* Col 4: Newsletter / Admissions Alert */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-3 opacity-90">
                {isNewsletter ? 'Entrance Exam Updates' : 'Admissions Desk'}
              </h4>
              <p className="text-xs opacity-75 mb-3 leading-relaxed">
                Receive instant notifications on JEE Main, NEET UG, and scholarship test dates.
              </p>
              <div className="flex items-center gap-1.5">
                <input
                  type="email"
                  placeholder="Enter parent's email..."
                  className="px-2.5 py-1.5 text-xs rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-white/50 flex-1"
                />
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-md text-xs font-bold text-white shadow-xs transition-opacity hover:opacity-90"
                  style={{ backgroundColor: accentColor }}
                >
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Bar: Copyright & Compliance */}
        {!isCompact && (
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] opacity-60">
            <div>{copyrightText} Powered by Helios Academic Platform.</div>
            <div className="flex items-center gap-4">
              <a href="#privacy" className="hover:underline">Privacy Policy</a>
              <a href="#terms" className="hover:underline">Terms of Enrollment</a>
              <a href="#refund" className="hover:underline">Refund Policy</a>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};
